import { toast } from 'sonner'
import { PageHeader } from '@/components/ds/PageHeader'
import { Callout } from '@/components/ui/Callout'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'
import { useChannels } from '@/api/endpoints/channels'
import {
  ACTIVE_BATCH_STATUSES,
  useCreateRecoveryBatch,
  useRecoveryBatchAction,
  useRecoveryBatches,
  useRecoveryControl,
  useRecoveryExperiment,
  useRecoveryPreview,
  useRecoveryStatus,
} from '@/api/endpoints/channelRecovery'
import { ArchiveWeakSection } from '@/features/recovery/components/ArchiveWeakSection'
import { BatchesSection } from '@/features/recovery/components/BatchesSection'
import { ExperimentSection } from '@/features/recovery/components/ExperimentSection'
import { isMissingOnServer } from '@/features/recovery/labels'
import { errorMessage } from '@/lib/errors'
import styles from './RecoveryPage.module.scss'

const ACTION_DONE: Record<string, string> = {
  cancel: 'Lote pausado.',
  resume: 'Lote de volta à fila.',
  undo: 'Desfazer na fila.',
}

/**
 * "Recuperar canal" (``/channels/:slug/recovery``): arquivar vídeos fracos,
 * os lotes e o experimento. Ver apps/backend/docs/plan-channel-recovery.md.
 */
export function RecoveryPage({ slug }: { slug: string }) {
  const { data: channels = [] } = useChannels()
  const channelName = channels.find((c) => c.slug === slug)?.name ?? slug

  const status = useRecoveryStatus()
  const batches = useRecoveryBatches(slug)
  const experiment = useRecoveryExperiment(slug)
  const preview = useRecoveryPreview(slug)
  const create = useCreateRecoveryBatch(slug)
  const action = useRecoveryBatchAction(slug)
  const control = useRecoveryControl(slug)

  const header = <PageHeader eyebrow="Ações do canal" title={`Recuperar canal · ${channelName}`} />

  if (isMissingOnServer(batches.error) || isMissingOnServer(status.error)) {
    return (
      <div className={styles.page}>
        {header}
        <Callout variant="neutral" title="Esta função ainda não está no servidor">
          O servidor ainda roda a versão de antes. Ela aparece aqui quando o backend for atualizado.
        </Callout>
      </div>
    )
  }

  const enabled = status.data?.enabled
  const list = batches.data ?? []
  const hasActiveBatch = list.some((b) => ACTIVE_BATCH_STATUSES.includes(b.status))

  return (
    <div className={styles.page}>
      {header}
      {enabled === false ? (
        <Callout variant="neutral" title="Desligado nesta máquina">
          A prévia e o painel funcionam; arquivar, retomar e desfazer ficam desligados até o
          CHANNEL_RECOVERY_ENABLED ser ligado no servidor.
        </Callout>
      ) : null}
      {status.data ? (
        <p className={styles.muted}>
          Orçamento de hoje: {status.data.used_today} de {status.data.daily_units} unidades da cota
          do YouTube.
        </p>
      ) : null}

      <ArchiveWeakSection
        enabled={enabled}
        preview={preview.data}
        previewPending={preview.isPending}
        onPreview={(criteria) => {
          preview.mutate(criteria, {
            onError: (err) => toast.error(errorMessage(err, 'Não consegui montar a prévia')),
          })
        }}
        creating={create.isPending}
        hasActiveBatch={hasActiveBatch}
        onConfirm={(input) => {
          create.mutate(input, {
            onSuccess: (batch) =>
              toast.success(`Lote #${String(batch.id)} criado com ${String(batch.total)} vídeos.`),
            onError: (err) => toast.error(errorMessage(err, 'Não consegui criar o lote')),
          })
        }}
      />

      {batches.isError ? (
        <QueryErrorNotice
          error={batches.error}
          label="os lotes"
          onRetry={() => {
            void batches.refetch()
          }}
        />
      ) : (
        <BatchesSection
          batches={list}
          enabled={enabled}
          busy={action.isPending}
          onAction={(batchId, kind) => {
            action.mutate(
              { batchId, action: kind },
              {
                onSuccess: () => toast.success(ACTION_DONE[kind] ?? 'Feito.'),
                onError: (err) => toast.error(errorMessage(err, 'Não consegui mudar o lote')),
              },
            )
          }}
        />
      )}

      {experiment.isError ? (
        <QueryErrorNotice
          error={experiment.error}
          label="o experimento"
          onRetry={() => {
            void experiment.refetch()
          }}
        />
      ) : (
        <ExperimentSection
          experiment={experiment.data}
          controlBusy={control.isPending}
          onToggleControl={(mark) => {
            control.mutate(mark, {
              onError: (err) => toast.error(errorMessage(err, 'Não consegui marcar o controle')),
            })
          }}
        />
      )}
    </div>
  )
}
